import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Dropdown, DropdownButton, Form, Row } from "react-bootstrap";
import { BiCalendar, BiXCircle } from "react-icons/bi";
import { SearchIcon } from '@primer/octicons-react'
import { DateTimePicker } from 'react-datetime-picker';
import SoundPickerDialog from './SoundPickerDialog';
import { axiosAuth_DATA_MGMT_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import configInfo from '../config/config.json';


const AlarmRuleAdminNewView = (props) => {

  // Utilities
  const { i18n, t } = useTranslation(['common', 'custom']);
  const navigate = useNavigate();

  // Initialize with actual JSON obj.  If not, will get error.
  const [alarmRuleAttrs, setAlarmRuleAttrs] = useState({
    id: '',
    code: '',
    description: '',
    class: '',
    type: '',
    //datasourceId: '',
    //extSysId: '',
    impactType: '',
    defaultLevel: '',
    broadcast: false,
    autoWorkorder: false,
    resumeTime: '',
    soundId: null,
    status: '',
    triggerLogic: {}
  });

  const [resumeTime, setResumeTime] = useState(null);

  const classOptions = [
    { key: "REALTIME",   value: "REALTIME" },
    { key: "PREDICTIVE", value: "PREDICTIVE" }
  ];

  const typeOptions = [
    { key: "DIRECT",  value: "DIRECT" },
    { key: "DERIVED", value: "DERIVED" }
  ];

  const impactTypeOptions = [
    { key: "LOCATION", value: "LOCATION" },
    { key: "ASSET",    value: "ASSET" },
    { key: "LIFT",     value: "LIFT" }
  ];

  const defaultLevelOptions = [
    { key: "LOW",      value: "LOW" },
    { key: "MEDIUM",   value: "MEDIUM" },
    { key: "HIGH",     value: "HIGH" },
    { key: "CRITICAL", value: "CRITICAL" }
  ];

  const statusOptions = [
    { key: "ACTIVE",    value: "ACTIVE" },
    { key: "SUSPENDED", value: "SUSPENDED" },
    { key: "DISABLED",  value: "DISABLED" }
  ];

  const [categoryList, setCategoryList] = useState([]);

  const [showSoundPickerDialog, setShowSoundPickerDialog] = useState(false);


  // UI Functions

  // Initialize state
  useEffect(() => {
    fetchCategories();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const fetchCategories = () => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.get("alarm-categories");

        resolve(res.data);

        if (res.status === 200) {
          setCategoryList(res.data);
        }
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }


  const [errMsg, setErrMsg] = useState('');

  const onAttrsInputChange = (e) => {
    setAlarmRuleAttrs({ ...alarmRuleAttrs, [e.target.name]: e.target.value })
  }

  const onCategoryClick = (categoryId) => {
    setAlarmRuleAttrs({ ...alarmRuleAttrs, categoryId: categoryId })
  }

  const onClassSelect = (key, event) => {
    setAlarmRuleAttrs({ ...alarmRuleAttrs, class: key })
  };

  const onTypeSelect = (key, event) => {
    setAlarmRuleAttrs({ ...alarmRuleAttrs, type: key })
  };

  const onImpactTypeSelect = (key, event) => {
    setAlarmRuleAttrs({ ...alarmRuleAttrs, impactType: key })
  };

  const onDefaultLevelSelect = (key, event) => {
    setAlarmRuleAttrs({ ...alarmRuleAttrs, defaultLevel: key })
  };

  const onStatusSelect = (key, event) => {
    setAlarmRuleAttrs({ ...alarmRuleAttrs, status: key })
  };

  const onCheckboxSelectNew = (e) => {
    setAlarmRuleAttrs({ ...alarmRuleAttrs, [e.target.name]: e.target.checked });
  }

  const onDatepickerChange = (value) => {
    let newTime = (new Date(value)).getTime();
    let currentTime = (new Date()).getTime();

    // ### This check is not necessary, since the UI already built guardrail.  But keep it here for now. ###
    if (!value && (value != null) && (newTime < currentTime)) {
      setErrMsg(t("I18N_ERR_Date_must_be_future"));
    } else if (value != null) {
      setErrMsg("");
      setAlarmRuleAttrs({ ...alarmRuleAttrs, resumeTime: value.toISOString()})
      setResumeTime(value);
    }
  }


  const handleSubmit = (e) => {
    e.preventDefault();

    if (!alarmRuleAttrs.code || alarmRuleAttrs.code.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_alarm_rule_code"));
    } else if (alarmRuleAttrs.code.length > 50) {
      setErrMsg(t("I18N_ERR_Max_len_code") + "50");
    } else {
      validateCode();
    }

    return;
  }

  // Since this is async, need to call this first, and check the others when username availability returns
  function validateCode() {

    new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.get("alarm-rules?code=" + alarmRuleAttrs.code);

        resolve(res.data);

        if (res.data.length > 0) {
          setErrMsg(t("I18N_ERR_AlarmRuleCode"));
        } else {
          validateOtherFields();
        }

        return;
      } catch (error) {

        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }

        return;
      }
    });
  }

  function validateOtherFields() {

    // Validate form inputs
    if (!alarmRuleAttrs.description || alarmRuleAttrs.description.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_description"));
    } else if (alarmRuleAttrs.description.length > 200) {
      setErrMsg(t("I18N_ERR_Max_len_desc") + "200");

    } else if (!alarmRuleAttrs.categoryId || alarmRuleAttrs.categoryId.length === 0) {
      setErrMsg(t("I18N_ERR_Need_alarm_category"));
    } else if (!alarmRuleAttrs.defaultLevel || alarmRuleAttrs.defaultLevel.length === 0) {
      setErrMsg(t("I18N_ERR_Need_alarm_default_level"));
    } else if (   configInfo.uiConfig.visibleFields.ALARMRULE_Class
               && (!alarmRuleAttrs.class || alarmRuleAttrs.class.length === 0)) {
      setErrMsg(t("I18N_ERR_Need_alarm_class"));
    } else if (!alarmRuleAttrs.type || alarmRuleAttrs.type.length === 0) {
      setErrMsg(t("I18N_ERR_Need_alarm_type"));
    } else if (!alarmRuleAttrs.impactType || alarmRuleAttrs.impactType.length === 0) {
      setErrMsg(t("I18N_ERR_Need_impact_type"));
    } else if (!alarmRuleAttrs.status || alarmRuleAttrs.status.length === 0) {
      setErrMsg(t("I18N_ERR_Need_alarm_status"));
/*
    } else if (!alarmRuleAttrs.triggerLogic || alarmRuleAttrs.triggerLogic.length === 0) {
      setErrMsg(t("I18N_ERR_Need_alarm_trigger_logic"));
    } else if (   (alarmRuleAttrs.triggerLogic !== '')
               && (!testJSON(alarmRuleAttrs.triggerLogic))) {
      setErrMsg(t("I18N_ERR_Trigger_logic_invalid_JSON"));
*/
    } else {
      setErrMsg('');
      createNewAlarmRule();
    }
  }

  const createNewAlarmRule = () => {

    if (!configInfo.uiConfig.visibleFields.ALARMRULE_Class) {
      // For customers who does not support explicit alarm classes, hardcoded using special rule

      // ##### THIS IS A TEMP HACK.  Long term solution maybe to introduce the Class concept for Alarm Category #####
      if (categoryList.find( item => item.id.toString() === alarmRuleAttrs.categoryId.toString()).code === 'PREDICTIVE_NOTICES') {
        alarmRuleAttrs.class = 'PREDICTIVE';
      } else {
        alarmRuleAttrs.class = 'REALTIME';
      }
    }

    // Make a deep copy
    const reqBody = JSON.parse(JSON.stringify(alarmRuleAttrs));

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.post("alarm-rules", reqBody);

        resolve(res.data);

        if (res.status === 201) {

          // Return to list
          navigate(-1);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }


  const handleCancel = (e) => {
    e.preventDefault();

    // Return to list
    navigate(-1);
  }


  // Sound picket dialog stuff

  const handleSoundPicker = (e) => {
    setShowSoundPickerDialog(true);
  }

  function handleSoundPickerClose() {
    setShowSoundPickerDialog(false);
  }

  function handleSoundSelect(sound) {

    let tmpAttrs = {};
    tmpAttrs.soundId   = sound.id;
    tmpAttrs.soundName = sound.name;

    setAlarmRuleAttrs({ ...alarmRuleAttrs, ...tmpAttrs});

    setShowSoundPickerDialog(false);
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_New_Alarm_Rule")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_AlarmRule_info")}
            </span>
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>
        </div>

        <Container>
          <form>
            <Row className={classes.admin_pages__field_label}>
              <Col sm="3">{t("I18N_AlarmRule_code")}: *</Col>
              <Col sm="3">{t("I18N_Description")}: *</Col>
              <Col sm="3">{t("I18N_Alarm_category")}: *</Col>
              <Col sm="3">{t("I18N_Default_level")}: *</Col>
            </Row>
            <Row className={classes.admin_pages__field_value}>
              <Col sm="3">
                <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="code" value={alarmRuleAttrs.code}
                  onChange={(e) => onAttrsInputChange(e)} />
              </Col>
              <Col sm="3">
                <textarea className={classes.admin_pages__field_editable} style={{width:'100%'}} name="description" value={alarmRuleAttrs.description}
                  onChange={(e) => onAttrsInputChange(e)} />
              </Col>
              <Col sm="3">
                <Dropdown>
                  <Dropdown.Toggle size="tiny_dropdown" variant="outline-secondary" className={classes.dropdown_item_wrap} id="dropdown-basic-button">
                    {!alarmRuleAttrs.categoryId ? t("common:I18N_DROPDOWN_PROMPT") :
                       (categoryList.find( ({ id }) => id === parseInt(alarmRuleAttrs.categoryId) )).code}
                  </Dropdown.Toggle>

                  <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto"}} id="dropdown-basic-button" >
                    {categoryList.map((item, index) => {
                      return (
                        <Dropdown.Item key={index} eventKey={item.id} className={classes.dropdown_item_wrap} onClick={() => onCategoryClick(item.id)}>
                          {item.code}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col sm="3">
                <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                  title={ !alarmRuleAttrs.defaultLevel ? t("common:I18N_DROPDOWN_PROMPT") : t("custom:I18N_ALM_LVL_" + alarmRuleAttrs.defaultLevel) }
                  onSelect={onDefaultLevelSelect}>
                  {defaultLevelOptions.map((item, index) => {
                    return (
                      <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                        {t("custom:I18N_ALM_LVL_" + item.value)}
                      </Dropdown.Item>
                    );
                  })}
                </DropdownButton>
              </Col>
            </Row>

            <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
              <Col>{t("I18N_Alarm_type")}: *</Col>
              <Col>{t("I18N_Alarm_impact_type")}: *</Col>
              { configInfo.uiConfig.visibleFields.ALARMRULE_Class ?
                  <Col> {t("I18N_Alarm_class")}: * </Col>
                :
                  <Col></Col>
              }
              { configInfo.uiConfig.visibleFields.ALARMRULE_Sound ?
                  <Col>{t("I18N_Alarm_sound")}:</Col>
                :
                  <Col></Col>
              }
            </Row>
            <Row className={classes.admin_pages__field_value}>
              <Col>
                <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                  title={ !alarmRuleAttrs.type ? t("common:I18N_DROPDOWN_PROMPT") : t("common:I18N_" + alarmRuleAttrs.type) }
                  onSelect={onTypeSelect}>
                  {typeOptions.map((item, index) => {
                    return (
                      <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                        {t("common:I18N_" + item.value)}
                      </Dropdown.Item>
                    );
                  })}
                </DropdownButton>
              </Col>
              <Col>
                <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                  title={ !alarmRuleAttrs.impactType ? t("common:I18N_DROPDOWN_PROMPT") : t("common:I18N_" + alarmRuleAttrs.impactType) }
                  onSelect={onImpactTypeSelect}>
                  {impactTypeOptions.map((item, index) => {
                    return (
                      <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                        {t("common:I18N_" + item.value)}
                      </Dropdown.Item>
                    );
                  })}
                </DropdownButton>
              </Col>
              <Col>
                { configInfo.uiConfig.visibleFields.ALARMRULE_Class ?
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={ !alarmRuleAttrs.class ? t("common:I18N_DROPDOWN_PROMPT") : t("common:I18N_" + alarmRuleAttrs.class) }
                      onSelect={onClassSelect}>
                      {classOptions.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("common:I18N_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  : null
                }
              </Col>
              <Col>
                { configInfo.uiConfig.visibleFields.ALARMRULE_Sound ?
                  <>
                    <input readOnly className={classes.admin_pages__field_editable} style={{width:'80%'}} name="soundFile"
                      value={alarmRuleAttrs.soundNa ? alarmRuleAttrs.soundName : t("I18N_Default_sound")}
                      onChange={(e) => onAttrsInputChange(e)} />
                    <span onClick={handleSoundPicker} style={{ marginLeft:'10px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                      <SearchIcon size={14} />
                    </span>
                  </>
                  : null
                }
              </Col>
            </Row>

            <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
{/*
              <Col>{t("I18N_Broadcast_alarm")}:</Col>
*/}
              { configInfo.uiConfig.visibleFields.ALARMRULE_AutoWorkorder ?
                <Col>{t("I18N_Alarm_auto_workorder")}:</Col>
              : null
              }
              <Col>{t("I18N_Status")}: *</Col>
              { alarmRuleAttrs.status && (alarmRuleAttrs.status === 'SUSPENDED') ?
              <Col>{t("I18N_Alarm_resume_time")}:</Col>
              :
                <Col></Col>
              }
              <Col></Col>
            </Row>
            <Row className={classes.admin_pages__field_value}>
{/*
              <Col><Form.Check type="switch" name={"broadcast"} checked={alarmRuleAttrs.broadcast}
                   onChange={onCheckboxSelectNew} />
              </Col>
*/}
              { configInfo.uiConfig.visibleFields.ALARMRULE_AutoWorkorder ?
                <Col><Form.Check type="switch" name={"autoWorkorder"} checked={alarmRuleAttrs.autoWorkorder}
                     onChange={onCheckboxSelectNew} />
                </Col>
              : null
              }
              <Col>
                <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                  title={ !alarmRuleAttrs.status ? t("common:I18N_DROPDOWN_PROMPT") : t("common:I18N_" + alarmRuleAttrs.status) }
                  onSelect={onStatusSelect}>
                  {statusOptions.map((item, index) => {
                    return (
                      <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                        {t("common:I18N_" + item.value)}
                      </Dropdown.Item>
                    );
                  })}
                </DropdownButton>
              </Col>
              { alarmRuleAttrs.status && (alarmRuleAttrs.status === 'SUSPENDED') ?
                <Col>
{/*
                  <input className={classes.admin_pages__field_editable} name="resumeTime" value={alarmRuleAttrs.resumeTime}
                    onChange={(e) => onAttrsInputChange(e)} />
*/}
                  <DateTimePicker onChange={onDatepickerChange} value={resumeTime ? resumeTime : ''}
                     format={ (resumeTime != null) ? "y-MM-dd h:00 a" : "y-MM-dd h:-- a" }
                     minDate={new Date(3600000 * ( Math.round(Date.now() / 3600000)))}
                     disableClock="true"
                     clearIcon=<BiXCircle size={16} style={{color:"gray"}}/>
                     calendarIcon=<BiCalendar size={16} style={{color:"gray"}}/>
                     locale={i18n.language === 'zh_HK' ? "zh-HK" : (i18n.language === 'zh_CN' ? "zh-CN" : "en")}
                  />
                </Col>
              :
                <Col></Col>
              }
              <Col></Col>
            </Row>

{/*
            <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
              <Col sm={{span: 12}}>{t("I18N_Trigger_logic")}: *</Col>
            </Row>
            <Row className={classes.admin_pages__field_value}>
              <Col sm={{span: 12}}>
                <textarea rows={5} className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="triggerLogic"
                          value={alarmRuleAttrs.triggerLogic} onChange={(e) => onAttrsInputChange(e)} />
              </Col>
            </Row>
*/}
            <Row className={classes.admin_pages__field_value} style={{ marginTop: '20px', fontStyle: 'italic' }}>
              <Col sm={{span: 12}}>{t("I18N_AlarmRule_New_notice")}</Col>
            </Row>

            <hr className="solid" style={{ marginTop: '30px' }}></hr>

            <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px', fontStyle: 'italic' }}>
              <span>{t("I18N_Req_fields")}</span>
            </Row>

            <Row>
              <div display='flex' style={{ marginTop: '15px' }}>
                <Button size="sm" variant="outline-secondary" className="mb-3"
                  style={{ float: 'left' }}
                  onClick={handleCancel}>
                  {t("I18N_Cancel")}
                </Button>
                <Button size="sm" variant="outline-secondary" className="mb-3"
                  style={{ marginLeft: '15px' }}
                  onClick={handleSubmit}>
                  {t("I18N_Submit")}
                </Button>
                <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                  {errMsg}
                </span>
              </div>
            </Row>
          </form>
        </Container>

        <SoundPickerDialog
          show={showSoundPickerDialog}
          handleObjSelect={handleSoundSelect}
          onClose={handleSoundPickerClose}
        />

      </Card>
    </>
  );
}

export default AlarmRuleAdminNewView;
